import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { List } from '@epicuro-next/models/engagement';
import { loadListsReports } from '@epicuro-next/state/analytics-entities';
import { upsertManyListEntities } from '@epicuro-next/state/list-entities';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import {
  upsertUserEntities,
  UserEntity,
} from '@epicuro-next/state/user-entities';
import { logout } from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { interval, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mapTo,
  startWith,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  withLatestFrom,
} from 'rxjs/operators';

import * as actions from '../../actions';
import {
  selectIsLoadingProductLists,
  selectOpenedList,
  selectParams,
  selectProductList,
} from '../../selectors';
import { selectFullLoaded } from '../../selectors/lists/product-lists.selectors';

const timerPolling = 10000;
const maxNumberOfAttempts = 20;

/**
 * ProductList Load Effects
 */
@Injectable()
export class LoadEffects {
  private accountId$ = this.store.pipe(
    select(selectAccountId),
    filter(isNonNullish),
  );
  private siteId$ = this.store.pipe(select(selectSiteId), filter(isNonNullish));

  /**
   * Try to cancel product list polling action
   */
  public checkCancelProductListPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.checkCancelProductListPolling),
      filter(
        (action) =>
          action.oldList !== undefined &&
          action.oldList.update.dateUtc !== action.newList.update.dateUtc,
      ),
      switchMap(() => of(actions.cancelProductListPolling())),
    ),
  );

  /**
   * Effect to load a ProductList with polling
   */
  public loadProductListPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductListPolling),
      switchMap((action) =>
        interval(timerPolling).pipe(
          startWith(0),
          takeWhile((attempts) => attempts < maxNumberOfAttempts, false),
          takeUntil(
            this.actions$.pipe(ofType(actions.cancelProductListPolling)),
          ),
          mapTo(
            actions.loadProductList({
              id: action.id,
            }),
          ),
        ),
      ),
    ),
  );

  /**
   * Effect to load the ProductLists
   */

  public loadProductLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductLists),
      concatLatestFrom(() => [
        this.store.pipe(select(selectParams)),
        this.store.pipe(select(selectIsLoadingProductLists)),
        this.store.pipe(select(selectFullLoaded)),
        this.accountId$,
        this.siteId$,
      ]),
      map(([, params, isLoading, isFullLoaded, accountId, siteId]) => ({
        params,
        isLoading,
        isFullLoaded,
        accountId,
        siteId,
      })),
      filter(({ isLoading, isFullLoaded }) => !isLoading || !isFullLoaded),
      switchMap(({ params, accountId, siteId }) =>
        this.productListApi.fetch({ ...params, accountId, siteId }).pipe(
          map((res) => ({ total: res?.total ?? 0, items: res?.items ?? [] })),
          map((res) => actions.loadProductListsSuccess(res)),
        ),
      ),
      catchError(() => of(actions.loadProductListsFailure())),
    ),
  );

  public loadProductListsAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductListsSuccess),
      map((lists) =>
        loadListsReports({ listIds: lists.items.map((item) => item.id) }),
      ),
    ),
  );

  /**
   * Effect to get a ProductList
   */
  public loadProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductList),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(selectProductList(action.id))),
            this.store.pipe(select(selectOpenedList)),
            this.accountId$,
            this.siteId$,
          ),
          switchMap(([_, oldList, openedList, accountId, siteId]) =>
            this.productListApi
              .get({ productListId: action.id, accountId, siteId })
              .pipe(
                take(1),
                switchMap((list: List) =>
                  !list
                    ? of(actions.loadProductListFailure())
                    : [
                        actions.loadProductListSuccess({
                          list,
                          oldList,
                        }),
                        ...(openedList?.id === action.id
                          ? [
                              actions.setOpenedListSuccess({
                                list,
                              }),
                            ]
                          : []),
                      ],
                ),
                catchError(() => of(actions.loadProductListFailure())),
              ),
          ),
        ),
      ),
    ),
  );

  /**
   * Effect to get the request parameters from the url.
   */
  public cancelProductListPollingOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      map(() => actions.cancelProductListPolling()),
    ),
  );

  /**
   * Effect to perform checks when product list successfully load
   */
  public loadProductListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductListSuccess),
      map((action) =>
        actions.checkCancelProductListPolling({
          newList: action.list,
          oldList: action.oldList,
        }),
      ),
    ),
  );

  public insertListEntitiesOnLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductListsSuccess),
      map((action) =>
        upsertManyListEntities({
          listEntities: action.items.map((item) => ({
            listId: item.id,
            list: item,
            loading: false,
            loadingProducts: false,
            reorderingCount: 0,
          })),
        }),
      ),
    ),
  );

  public insertUserEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductListsSuccess),
      map((action) =>
        upsertUserEntities({
          entities: action.items
            .filter((item) => item.creation.user)
            .map(
              (item) =>
                ({
                  userExternalId: item.creation.user.externalId,
                  user: item.creation.user,
                } as UserEntity),
            ),
        }),
      ),
    ),
  );

  /**
   * Dependencies
   * @param actions$ required by ngRx
   * @param productListService Service to make the HttpRequests
   */
  constructor(
    private store: Store,
    private actions$: Actions,
    private productListApi: ProductListApiService,
  ) {}
}
